import { DriverAppDownloadMessageStatusEnum, type IDriverAppDownloadMessageAction } from '../../../types/tracking'

const toEpoch = (date?: string | null) => {
  if (!date) return 0

  const parsed = new Date(date).getTime()
  return Number.isNaN(parsed) ? 0 : parsed
}

const latestStatusAt = (action: IDriverAppDownloadMessageAction) => {
  return Math.max(
    toEpoch(action.canceledAt),
    toEpoch(action.completedAt),
    toEpoch(action.scheduledAt),
    toEpoch(action.messageDueDate)
  )
}

const resolveStatus = (action: IDriverAppDownloadMessageAction): DriverAppDownloadMessageStatusEnum => {
  if (action.canceledAt && toEpoch(action.canceledAt) >= toEpoch(action.completedAt)) {
    return DriverAppDownloadMessageStatusEnum.Canceled
  }

  if (action.completedAt) {
    return DriverAppDownloadMessageStatusEnum.Completed
  }

  return DriverAppDownloadMessageStatusEnum.Scheduled
}

export const normalizeAppDownloadActions = (actions?: IDriverAppDownloadMessageAction[]) => {
  if (!actions?.length) return [] as IDriverAppDownloadMessageAction[]

  const bySequence = new Map<number, IDriverAppDownloadMessageAction>()

  actions.forEach((action) => {
    const existing = bySequence.get(action.sequence)

    if (!existing) {
      bySequence.set(action.sequence, {
        ...action
      })
      return
    }

    bySequence.set(action.sequence, {
      ...existing,
      actionID: existing.actionID || action.actionID,
      canceledAt:
        toEpoch(action.canceledAt) > toEpoch(existing.canceledAt)
          ? action.canceledAt || existing.canceledAt
          : existing.canceledAt,
      completedAt:
        toEpoch(action.completedAt) > toEpoch(existing.completedAt)
          ? action.completedAt || existing.completedAt
          : existing.completedAt,
      messageDueDate:
        toEpoch(action.messageDueDate) > toEpoch(existing.messageDueDate)
          ? action.messageDueDate
          : existing.messageDueDate,
      scheduledAt:
        toEpoch(action.scheduledAt) > toEpoch(existing.scheduledAt)
          ? action.scheduledAt || existing.scheduledAt
          : existing.scheduledAt,
      statusDescription: action.statusDescription || existing.statusDescription
    })
  })

  return [...bySequence.values()]
    .map((action) => ({
      ...action,
      status: resolveStatus(action)
    }))
    .sort((a, b) => a.sequence - b.sequence)
}

export const getLatestAppDownloadAction = (actions?: IDriverAppDownloadMessageAction[]) => {
  const normalizedActions = normalizeAppDownloadActions(actions)

  if (normalizedActions.length === 0) return null

  return normalizedActions
    .slice()
    .sort((a, b) => {
      const timeDiff = latestStatusAt(a) - latestStatusAt(b)
      if (timeDiff !== 0) return timeDiff
      return a.sequence - b.sequence
    })
    .pop() as IDriverAppDownloadMessageAction
}
